'use client';


import { FaHeart, FaRegHeart } from "react-icons/fa";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { addToFavorites, removeFromFavorites } from "@/services/favorites";
import { addFavoriteItem, removeFavoriteItem, $shopState } from "@/shared/store/shop";
import { useUnit } from "effector-react";
import { Product } from "@/types/product";
import { Icon, IconButton, IconButtonProps } from "@chakra-ui/react";
import { GiftBox } from "@/types/giftbox";
import BookmarkSvgIcon from "@/shared/icons/BookmarkSvgIcon";
import { useAnalytics } from "@/hooks/useAnalytics";

type Props = {
  product: Product | null;
} & IconButtonProps;

export default function ProductFavoriteButton({ product, ...props }: Props) {
  if (!product) return null;
  const { track } = useAnalytics();
  const queryClient = useQueryClient();

  const { favorites } = useUnit($shopState);
  const isInFavorites = favorites.items.some(item => item.product?.id === product.id);

  const addToFavoritesMutation = useMutation({
    mutationFn: async () => {
      const favoriteItem = await addToFavorites({ productId: product.id });
      return favoriteItem;
    },
    onSuccess: (favoriteItem) => {
      addFavoriteItem({ ...favoriteItem, product });
    },
  });

  const removeFromFavoritesMutation = useMutation({
    mutationFn: async () => {
      const favoriteItem = await removeFromFavorites(favorites.items.find(item => item.product?.id === product.id)?.id!);
      return favoriteItem;
    },
    onSuccess: (favoriteItem) => {
      queryClient.invalidateQueries({ queryKey: ["favorites"] });
      removeFavoriteItem(favoriteItem.id);
    },
  });

  return isInFavorites ? (
    <IconButton
      className="group"
      variant="plain"
      borderRadius="none"
      aria-label="Remove from favorites"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        track({
          type: 'remove_from_favorites',
          data: {
            product,
          },
        });
        removeFromFavoritesMutation.mutate();
      }}
      disabled={removeFromFavoritesMutation.isPending}
      loading={removeFromFavoritesMutation.isPending}
      children={
        <Icon
          children={<BookmarkSvgIcon />}
          _groupHover={{
            stroke: "black",
          }}
        />
      }
      {...props}
    />
  ) : (
    <IconButton
      className="group"
      variant="plain"
      borderRadius="none"
      aria-label="Add to favorites"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        track({
          type: 'add_to_favorites',
          data: {
            product,
          },
        });
        addToFavoritesMutation.mutate();
      }}
      disabled={addToFavoritesMutation.isPending}
      loading={addToFavoritesMutation.isPending}
        {...props}
    >
      <Icon
          fill={"none"}
          stroke={"black"}
        _groupHover={{
          stroke: "accent",
          fill: "accent.secondary",
        }}>
          <BookmarkSvgIcon />
      </Icon>
    </IconButton>
  );
}